import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Pagination,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaCalendarAlt,
  FaSearch,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheck,
  FaChartBar,
  FaDownload,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";

const BookingsDashboard = () => {
  const navigate = useNavigate();
  const { isSubadmin } = useAuth();

  // State
  const [bookings, setBookings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Filters / UI
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pincodeFilter, setPincodeFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal
  const [selected, setSelected] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Summary cards
  const [summary, setSummary] = useState({
    total: 0,
    totalAmount: 0,
    paidCount: 0,
  });

  // Helper: normalize status strings (e.g. "booking_status.pending" -> "pending")
  const normalizeStatus = (s) => {
    if (!s && s !== "") return "unknown";
    if (typeof s !== "string") return String(s);
    return s.replace(/^booking_status\./i, "").toLowerCase();
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const bases = Array.from(
          new Set(
            [
              process.env.REACT_APP_API_BASE_URL,
              "https://api.quvikme.com",
            ].filter(Boolean)
          )
        );

        const adminToken =
          localStorage.getItem("adminToken") ||
          sessionStorage.getItem("adminToken");

        let data = null;
        let lastErr = null;

        // try each base
        for (const base of bases) {
          try {
            const url = `${base}/api/bookings/all?limit=500`;
            const res = await fetch(url, {
              headers: {
                "Content-Type": "application/json",
                ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
              },
            });
            if (!res.ok) {
              lastErr = new Error(
                `Failed to load bookings from ${base}: ${res.status}`
              );
              continue;
            }
            const json = await res.json();
            // Accept { bookings: [...] } or direct array
            data = Array.isArray(json) ? json : json.bookings || [];
            break;
          } catch (e) {
            lastErr = e;
            continue;
          }
        }

        if (!data)
          throw (
            lastErr || new Error("Failed to load bookings from any endpoint")
          );

        // Normalize and keep raw for modal
        const normalized = data.map((b, idx) => {
          const raw = b || {};
          const id = raw.id || raw.bookingId || raw._id || `bk-${idx}`;
          const bookingId = raw.bookingId || raw.id || raw._id || id;
          const date =
            raw.date ||
            raw.createdAt ||
            raw.created_at ||
            raw.dateTime ||
            raw.date_time ||
            new Date().toISOString();
          const statusRaw =
            raw.status || raw.bookingStatus || raw.status_code || "unknown";
          const status = normalizeStatus(statusRaw);
          const amount =
            raw.amount ?? raw.price ?? raw.total ?? raw.paymentAmount ?? 0;
          const paymentStatus = (
            raw.paymentStatus ||
            raw.payment_status ||
            (raw.paid ? "paid" : "unpaid") ||
            "unknown"
          ).toLowerCase();
          const vendorName =
            raw.vendorName ||
            raw.vendor?.name ||
            raw.vendor_name ||
            raw.businessName ||
            "Unknown Vendor";
          const customerName =
            raw.customerName ||
            raw.userName ||
            raw.customer?.name ||
            raw.customer_name ||
            "Unknown Customer";
          const pincode =
            raw.pincode || raw.vendorPincode || raw.customerPincode || "";

          return {
            id,
            bookingId,
            date,
            status,
            statusRaw,
            amount,
            paymentStatus,
            vendorName,
            customerName,
            serviceName:
              raw.serviceName || raw.service?.name || raw.service_name || "",
            pincode,
            raw,
          };
        });

        // Summary
        const total = normalized.length;
        const totalAmount = normalized.reduce(
          (s, x) => s + (Number(x.amount) || 0),
          0
        );
        const paidCount = normalized.filter(
          (x) => x.paymentStatus === "paid"
        ).length;

        setBookings(normalized);
        setFiltered(normalized);
        setSummary({ total, totalAmount, paidCount });
        setCurrentPage(1);
      } catch (err) {
        console.error("Error loading bookings:", err);
        setErrorMessage(err.message || "Failed to load bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Filtering & search (client-side)
  useEffect(() => {
    const s = searchTerm.trim().toLowerCase();
    const p = pincodeFilter.trim();

    const f = bookings.filter((b) => {
      // status filter: 'all' or exact normalized match
      const statusMatch = statusFilter === "all" || b.status === statusFilter;

      // pincode partial match (startsWith)
      const pincodeMatch =
        !p || (b.pincode && b.pincode.toString().startsWith(p));

      // search across bookingId, vendor, customer, service
      const searchMatch =
        !s ||
        (b.bookingId && b.bookingId.toLowerCase().includes(s)) ||
        (b.vendorName && b.vendorName.toLowerCase().includes(s)) ||
        (b.customerName && b.customerName.toLowerCase().includes(s)) ||
        (b.serviceName && b.serviceName.toLowerCase().includes(s)) ||
        (b.pincode && b.pincode.toString().includes(s));

      return statusMatch && pincodeMatch && searchMatch;
    });

    setFiltered(f);
    setCurrentPage(1);
  }, [bookings, searchTerm, statusFilter, pincodeFilter]);

  // Pagination calculations
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentRows = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  // View modal
  const handleView = (b) => {
    setSelected(b);
    setShowViewModal(true);
  };

  const handleDelete = async (booking) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete booking ${booking.bookingId}?`
    );
    if (!confirmDelete) return;

    try {
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const base =
        process.env.REACT_APP_API_BASE_URL || "https://api.quvikme.com";

      const res = await fetch(`${base}/api/bookings/admin/${booking.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to delete booking");
      }

      // ✅ Remove from UI instantly
      const updated = bookings.filter((b) => b.id !== booking.id);
      setBookings(updated);
      setFiltered(updated);

      setSuccessMessage("Booking deleted successfully ✅");
    } catch (err) {
      console.error("Delete error:", err);
      setErrorMessage(err.message);
    }
  };

  // Export to Excel
  const downloadReport = () => {
    const rows = filtered.map((b) => ({
      "Booking ID": b.bookingId,
      Date: new Date(b.date).toLocaleString(),
      Status: b.status,
      "Vendor Name": b.vendorName,
      "Customer Name": b.customerName,
      Service: b.serviceName,
      Amount: b.amount,
      "Payment Status": b.paymentStatus,
      Pincode: b.pincode || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, "bookings-report.xlsx");
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>
            <FaCalendarAlt className="me-2" />
            Bookings
          </h2>
          <p className="text-muted">Minimal view — quick checks and exports</p>
        </Col>
      </Row>

      {/* Alerts */}
      {errorMessage && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant="danger"
              dismissible
              onClose={() => setErrorMessage("")}
            >
              <FaTimes className="me-2" />
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      {successMessage && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccessMessage("")}
            >
              <FaCheck className="me-2" />
              {successMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaChartBar className="text-primary mb-2" size={20} />
              <h4>{summary.total}</h4>
              <p className="text-muted mb-0">Total Bookings</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaDownload className="text-success mb-2" size={20} />
              <h4>₹{summary.totalAmount?.toLocaleString?.() || 0}</h4>
              <p className="text-muted mb-0">Total Amount</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaCheck className="text-success mb-2" size={20} />
              <h4>{summary.paidCount}</h4>
              <p className="text-muted mb-0">Paid</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="d-flex align-items-center justify-content-end">
          <Button variant="outline-success" onClick={downloadReport}>
            <FaFileExcel className="me-2" /> Export to Excel
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search bookingId, vendor, customer, service or pincode"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by Pincode (partial match)"
            value={pincodeFilter}
            onChange={(e) => setPincodeFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="pending">pending</option>
            <option value="confirmed">confirmed</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
            <option value="unknown">unknown</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Bookings ({filtered.length})</h5>
        </Card.Header>

        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Date</th>
                <th>Status</th>
                <th>Vendor</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((b) => (
                <tr key={b.id}>
                  <td style={{ minWidth: 180 }}>{b.bookingId}</td>
                  <td>{new Date(b.date).toLocaleString()}</td>
                  <td>
                    <Badge
                      bg={
                        b.status === "completed"
                          ? "success"
                          : b.status === "cancelled"
                            ? "danger"
                            : b.status === "pending"
                              ? "warning"
                              : "secondary"
                      }
                    >
                      {b.status}
                    </Badge>
                  </td>
                  <td>{b.vendorName}</td>
                  <td>{b.customerName}</td>
                  <td>{b.serviceName || "—"}</td>
                  <td>₹{(b.amount || 0).toLocaleString()}</td>
                  <td>
                    <Badge
                      bg={b.paymentStatus === "paid" ? "success" : "warning"}
                    >
                      {b.paymentStatus}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1 mb-1"
                      onClick={() => handleView(b)}
                    >
                      <FaEye />
                    </Button>

                    {!isSubadmin && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="me-1 mb-1"
                        onClick={() => handleDelete(b)}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (p) => (
                    <Pagination.Item
                      key={p}
                      active={p === currentPage}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* View Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <Row>
              <Col md={6}>
                <h6>Basic</h6>
                <p>
                  <strong>Booking ID:</strong> {selected.bookingId}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {new Date(selected.date).toLocaleString()}
                </p>
                <p>
                  <strong>Status:</strong> {selected.status}
                </p>
                <p>
                  <strong>Amount:</strong> ₹
                  {(selected.amount || 0).toLocaleString()}
                </p>
                <p>
                  <strong>Payment:</strong> {selected.paymentStatus}
                </p>
              </Col>
              <Col md={6}>
                <h6>Participants</h6>
                <p>
                  <strong>Vendor:</strong> {selected.vendorName}
                </p>
                <p>
                  <strong>Customer:</strong> {selected.customerName}
                </p>
                <p>
                  <strong>Service:</strong> {selected.serviceName || "—"}
                </p>
                <p>
                  <strong>Pincode:</strong> {selected.pincode || "—"}
                </p>
              </Col>

              <Col md={12} className="mt-3">
                <h6>Raw booking object</h6>
                <pre
                  style={{
                    maxHeight: 300,
                    overflow: "auto",
                    background: "#f8f9fa",
                    padding: 12,
                  }}
                >
                  {JSON.stringify(selected.raw, null, 2)}
                </pre>
              </Col>
            </Row>
          ) : (
            <div>No booking selected</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BookingsDashboard;
